import {
  ChangeEvent,
  ClipboardEvent,
  KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from "react";

type PinInputProps = {
  value: string;
  onChange: (value: string) => void;
  length?: number;
  disabled?: boolean;
};

/**
 * Segmented PIN entry — one rounded box per digit, with auto-advance, paste
 * support and arrow-key navigation.
 *
 * The boxes are held as a fixed-length array internally rather than being
 * derived from `value`. Deriving them would shift digits leftward whenever a
 * middle box is cleared. Joining gaps out means a partially-filled PIN is
 * shorter than `length`, which is what the caller checks before submitting.
 */
export default function PinInput({
  value,
  onChange,
  length = 5,
  disabled = false,
}: PinInputProps) {
  const [chars, setChars] = useState<string[]>(() =>
    Array.from({ length }, (_, i) => value[i] ?? "")
  );
  const inputs = useRef<Array<HTMLInputElement | null>>([]);

  // Let the caller reset the field by clearing the value.
  useEffect(() => {
    if (value === "") setChars(Array.from({ length }, () => ""));
  }, [value, length]);

  const focusAt = (i: number) => {
    const el = inputs.current[Math.max(0, Math.min(length - 1, i))];
    el?.focus();
    el?.select();
  };

  const commit = (next: string[]) => {
    setChars(next);
    onChange(next.join(""));
  };

  const handleChange =
    (i: number) => (e: ChangeEvent<HTMLInputElement>) => {
      const digits = e.target.value.replace(/\D/g, "");
      const next = [...chars];

      if (!digits) {
        next[i] = "";
        commit(next);
        return;
      }

      // More than one digit means it was pasted or typed fast — spread it.
      let cursor = i;
      for (const digit of digits) {
        if (cursor >= length) break;
        next[cursor] = digit;
        cursor += 1;
      }

      commit(next);
      focusAt(cursor);
    };

  const handleKeyDown =
    (i: number) => (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Backspace") {
        e.preventDefault();
        const next = [...chars];

        if (next[i]) {
          next[i] = "";
          commit(next);
        } else if (i > 0) {
          next[i - 1] = "";
          commit(next);
          focusAt(i - 1);
        }
        return;
      }

      if (e.key === "ArrowLeft" && i > 0) {
        e.preventDefault();
        focusAt(i - 1);
      }

      if (e.key === "ArrowRight" && i < length - 1) {
        e.preventDefault();
        focusAt(i + 1);
      }
    };

  const handlePaste = (e: ClipboardEvent<HTMLDivElement>) => {
    const digits = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, length);

    if (!digits) return;

    e.preventDefault();
    const next = Array.from({ length }, () => "");
    digits.split("").forEach((digit, i) => {
      next[i] = digit;
    });

    commit(next);
    focusAt(digits.length);
  };

  return (
    <div className="pin-input" onPaste={handlePaste}>
      {chars.map((char, i) => (
        <input
          key={i}
          ref={(el) => {
            inputs.current[i] = el;
          }}
          className={`pin-box${char ? " pin-box--filled" : ""}`}
          type="password"
          inputMode="numeric"
          autoComplete="off"
          maxLength={1}
          value={char}
          disabled={disabled}
          onChange={handleChange(i)}
          onKeyDown={handleKeyDown(i)}
          onFocus={(e) => e.target.select()}
          aria-label={`PIN digit ${i + 1} of ${length}`}
        />
      ))}
    </div>
  );
}
